import { CdkTrapFocus } from '@angular/cdk/a11y';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  input,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';

import { Icon } from '../icon/icon';
import { LayoutService } from './layout.service';

/**
 * Application frame: collapsible sidenav on the left, routed content on the
 * right. On handset viewports the sidenav becomes an overlay drawer behind a
 * top bar.
 *
 * Usage — project a `<ui-sidenav>` plus the page content:
 *
 * ```html
 * <ui-app-shell>
 *   <ui-sidenav [sections]="sections">…</ui-sidenav>
 *   <span uiShellTitle>Partner</span>
 *   <router-outlet />
 * </ui-app-shell>
 * ```
 *
 * Anything not matching a named slot becomes the main content.
 */
@Component({
  selector: 'ui-app-shell',
  imports: [CdkTrapFocus, Icon],
  templateUrl: './app-shell.html',
  styleUrl: './app-shell.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(document:keydown.escape)': 'onEscape()',
  },
})
export class AppShell {
  /** Label for the skip link that jumps past the navigation. */
  readonly skipLinkLabel = input<string>('Skip to main content');

  protected readonly layout = inject(LayoutService);
  private readonly router = inject(Router);

  private readonly main = viewChild.required<ElementRef<HTMLElement>>('main');

  constructor() {
    // Navigating always dismisses the handset drawer so content is revealed.
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe(() => this.layout.closeDrawer());
  }

  /** Escape dismisses the drawer; a no-op guard keeps the document-level
   *  listener free on desktop, where no drawer exists to close. */
  protected onEscape(): void {
    if (this.layout.drawerOpen()) {
      this.layout.closeDrawer();
    }
  }

  /**
   * The skip link cannot rely on its fragment href: with `<base href="/">`
   * the fragment resolves against the base, so on any route other than the
   * root it becomes a cross-document link and activating it triggers a full
   * reload — punishing exactly the keyboard users it exists to serve. Focus
   * is moved imperatively instead (`<main>` carries tabindex="-1" for this);
   * the href stays as a meaningful target for assistive tech.
   */
  protected skipToMain(event: Event): void {
    event.preventDefault();
    this.main().nativeElement.focus();
  }
}
